'use client'

import { useEffect, useRef, useState } from 'react'
import { clearChromeSession, LEGAL_LINKS, loginHref, readChromeSession } from './session.js'

/**
 * User profile menu — login/register or account + legal + logout.
 * Worlds can pass onLogin/onRegister to open their own auth instead of the hub.
 * @param {{
 *   lang?: 'en' | 'es',
 *   returnUrl?: string,
 *   onLogin?: () => void,
 *   onRegister?: () => void,
 *   onLogout?: () => void,
 *   showLogout?: boolean,
 * }} props
 */
export function UnityProfileMenu({
  lang = 'es',
  returnUrl,
  onLogin,
  onRegister,
  onLogout,
  showLogout = true,
  /** When true, sign-in lives in the page body — menu is legal/links only. */
  authInContent = false,
}) {
  const [open, setOpen] = useState(false)
  const [session, setSession] = useState(null)
  const rootRef = useRef(null)
  const es = lang !== 'en'

  useEffect(() => {
    const refresh = () => setSession(readChromeSession())
    refresh()
    const onStorage = (e) => {
      if (e.key === 'unity-cosmos-session') refresh()
    }
    window.addEventListener('storage', onStorage)
    window.addEventListener('focus', refresh)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('focus', refresh)
    }
  }, [])

  useEffect(() => {
    if (!open) return undefined
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const login = loginHref(returnUrl)
  const register = `${login}${login.includes('?') ? '&' : '?'}register=1`

  const logout = () => {
    if (onLogout) onLogout()
    clearChromeSession()
    setSession(null)
    setOpen(false)
    if (!onLogout) window.location.reload()
  }

  const handleLogin = (e) => {
    if (!onLogin) return
    e.preventDefault()
    setOpen(false)
    onLogin()
  }

  const handleRegister = (e) => {
    if (!onRegister && !onLogin) return
    e.preventDefault()
    setOpen(false)
    ;(onRegister || onLogin)?.()
  }

  const label = session?.name || session?.email || (es ? 'Cuenta' : 'Account')
  const initial = (label[0] || '?').toUpperCase()

  const guestChrome = authInContent && !session?.active

  return (
    <div className={`uc-profile${guestChrome ? ' uc-profile--auth-in-content' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="uc-profile__trigger"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={guestChrome ? (es ? 'Legal y privacidad' : 'Legal and privacy') : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="uc-profile__avatar" aria-hidden>
          {initial}
        </span>
        {guestChrome ? null : (
          <span className="uc-profile__label">{session?.active ? label : es ? 'Entrar' : 'Sign in'}</span>
        )}
        <span className={`uc-profile__chev${open ? ' is-open' : ''}`} aria-hidden>
          ▾
        </span>
      </button>

      {open ? (
        <div className="uc-profile__menu" role="menu">
          {session?.active ? (
            <>
              <p className="uc-profile__meta">{session.email}</p>
              <button type="button" className="uc-profile__item" role="menuitem" onClick={logout}>
                {es ? 'Cerrar sesión' : 'Log out'}
              </button>
            </>
          ) : guestChrome ? null : (
            <>
              <a
                className="uc-profile__item uc-profile__item--primary"
                role="menuitem"
                href={login}
                onClick={handleLogin}
              >
                {es ? 'Iniciar sesión' : 'Log in'}
              </a>
              <a className="uc-profile__item" role="menuitem" href={register} onClick={handleRegister}>
                {es ? 'Registrar cuenta' : 'Register'}
              </a>
            </>
          )}
          {!session?.active && guestChrome ? (
            <p className="uc-profile__meta">{es ? 'Legal' : 'Legal'}</p>
          ) : null}
          <div className="uc-profile__divider" role="separator" />
          <a className="uc-profile__item uc-profile__item--muted" role="menuitem" href={LEGAL_LINKS.privacy}>
            {es ? 'Privacidad' : 'Privacy'}
          </a>
          <a className="uc-profile__item uc-profile__item--muted" role="menuitem" href={LEGAL_LINKS.cookies}>
            Cookies
          </a>
          <a className="uc-profile__item uc-profile__item--muted" role="menuitem" href={LEGAL_LINKS.terms}>
            {es ? 'Términos' : 'Terms'}
          </a>
        </div>
      ) : null}
    </div>
  )
}
